import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Pencil, Plus, Trash2, Upload, Download, X, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatR } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { ImportReviewModal } from "@/components/ImportReviewModal";

const EMPTY = {
  name: "", sku: "", barcode: "", isbn: "", category: "stationery", subcategory: "",
  description: "", image: "", price: "", cost_price: "", promo_price: "",
  promo_start: "", promo_end: "", stock: 0, supplier: "", featured: false, status: "active",
};

const toForm = (p) => ({
  ...EMPTY, ...p,
  promo_start: p.promo_start || "", promo_end: p.promo_end || "",
  cost_price: p.cost_price ?? "", promo_price: p.promo_price ?? "",
  variant_label: p.variants?.[0]?.label || "",
  variant_options: (p.variants?.[0]?.options || []).join(", "),
});

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [missingOnly, setMissingOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [quick, setQuick] = useState({}); // {productId: {price, stock, cost_price}}
  const [publishing, setPublishing] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { categories, bySlug } = useCategories();

  const load = (query = q, status = statusFilter) =>
    api.get("/admin/products", { params: { ...(query ? { q: query } : {}), ...(status ? { status } : {}) } })
      .then((r) => setProducts(r.data.products)).catch(() => {});

  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: /admin-login/products?edit=<id> opens the full editor for that product
  // (used by the "Full edit" button in Schools & Lists)
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId) return;
    api.get(`/admin/products/${editId}`)
      .then((r) => { setEditing(toForm(r.data.product)); setSearchParams({}, { replace: true }); })
      .catch((err) => toast.error(errMsg(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setEditing((e) => ({ ...e, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const vOpts = (editing.variant_options || "").split(",").map((s) => s.trim()).filter(Boolean);
    const body = {
      ...editing,
      price: Number(editing.price) || 0,
      cost_price: editing.cost_price === "" ? null : Number(editing.cost_price),
      promo_price: editing.promo_price === "" ? null : Number(editing.promo_price),
      promo_start: editing.promo_start || null,
      promo_end: editing.promo_end || null,
      stock: Number(editing.stock) || 0,
      image: editing.image || bySlug[editing.category]?.image || "",
      variants: (editing.variant_label || "").trim() && vOpts.length
        ? [{ label: editing.variant_label.trim(), options: vOpts, required: true }]
        : [],
    };
    delete body.variant_label;
    delete body.variant_options;
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, body);
      else await api.post("/admin/products", body);
      toast.success(editing.id ? "Product updated — live on the store" : "Product created — live on the store");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success(`"${p.name}" discontinued`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  // ---- Rapid pricing for imported drafts: enter price + stock, publish to go live ----
  const qset = (id, patch) => setQuick((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const publish = async (p) => {
    const v = quick[p.id] || {};
    const price = Number(v.price);
    const stock = v.stock === "" || v.stock === undefined ? p.stock : Number(v.stock);
    if (!price || price <= 0) {
      toast.error("Enter a selling price above R0 before publishing");
      return;
    }
    setPublishing(p.id);
    try {
      await api.put(`/admin/products/${p.id}`, {
        ...p,
        price,
        cost_price: v.cost_price === "" || v.cost_price === undefined ? p.cost_price : Number(v.cost_price),
        stock: Number.isFinite(stock) ? stock : 0,
        status: "active",
      });
      toast.success(`"${p.name}" is live at ${formatR(price)} — now visible in the shop and school lists`);
      setQuick((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPublishing("");
    }
  };

  // ---- Bulk draft pricing: paste a supplier price list, preview matches, publish all ----
  const runBulk = async (apply) => {
    if (!bulkText.trim()) {
      toast.error("Paste the price list first");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/products/draft-price-bulk", { lines: bulkText, apply });
      setBulkPreview(data);
      if (apply) {
        toast.success(`${data.applied} item${data.applied !== 1 ? "s" : ""} published with prices — live on the shop now`);
        if (data.applied > 0) {
          setBulkText("");
          setBulkPreview(null);
          setBulkOpen(false);
          load();
        }
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const runImport = async () => {
    try {
      const { data } = await api.post("/admin/products/import", { csv_text: csvText });
      toast.success(`Import done: ${data.created} created, ${data.updated} updated${data.errors.length ? `, ${data.errors.length} errors` : ""}`);
      if (data.errors.length) console.warn(data.errors);
      setImportOpen(false);
      setCsvText("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const importFile = () => {
    setImportOpen(false);
    setReviewOpen(true);
  };

  const exportCsv = async () => {
    const r = await api.get("/admin/products-export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "btm-products.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const field = (label, key, opts = {}) => (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <input
        {...opts}
        value={editing[key] ?? ""}
        onChange={(e) => setEditing({ ...editing, [key]: opts.type === "checkbox" ? e.target.checked : e.target.value })}
        className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-gold"
        data-testid={`product-form-${key}`}
      />
    </label>
  );

  const missingCount = products.filter((p) => !p.image && p.status !== "discontinued").length;
  const shown = missingOnly ? products.filter((p) => !p.image && p.status !== "discontinued") : products;

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy">Products</h1>
          <p className="text-sm text-slate-500 mt-1">Every change here is published to the store immediately.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setImportOpen(true)} className="h-10 px-4 rounded-full bg-white border border-slate-200 text-sm font-semibold flex items-center gap-1.5" data-testid="import-products-btn">
            <Upload size={14} /> CSV import
          </button>
          <button onClick={exportCsv} className="h-10 px-4 rounded-full bg-white border border-slate-200 text-sm font-semibold flex items-center gap-1.5" data-testid="export-products-btn">
            <Download size={14} /> Export
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} className="h-10 px-4 rounded-full bg-navy text-white text-sm font-bold flex items-center gap-1.5" data-testid="add-product-btn">
            <Plus size={14} /> Add product
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search by name or SKU…"
          className="h-10 rounded-full border border-slate-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-gold w-64"
          data-testid="admin-product-search"
        />
        <button onClick={() => load()} className="h-10 px-4 rounded-full bg-white border border-slate-200 text-sm font-semibold" data-testid="admin-product-search-btn">Search</button>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); load(q, e.target.value); }}
          className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-navy outline-none"
          data-testid="admin-product-status-filter"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft (needs price)</option>
          <option value="discontinued">Discontinued</option>
        </select>
        <button
          onClick={() => setMissingOnly((m) => !m)}
          className={`h-10 px-4 rounded-full text-sm font-semibold flex items-center gap-1.5 border transition-colors ${
            missingOnly ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-amber-300 text-amber-700"
          }`}
          data-testid="filter-missing-images"
          title="Show only products that have no photo — click a product to edit and upload one"
        >
          <ImageOff size={14} /> Missing photos ({missingCount})
        </button>
      </div>

      {statusFilter !== "draft" && products.some((p) => p.status === "draft") && (
        <button
          onClick={() => { setStatusFilter("draft"); load(q, "draft"); }}
          className="w-full mb-4 rounded-2xl border border-gold/50 bg-gold/5 px-5 py-3.5 text-left flex items-center gap-3 hover:bg-gold/10 transition-colors"
          data-testid="drafts-banner"
        >
          <span className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-display font-bold text-sm">
            {products.filter((p) => p.status === "draft").length}
          </span>
          <span className="text-sm text-navy">
            <span className="font-bold">Imported items waiting for prices</span>
            <span className="text-slate-500"> — click here to open quick price entry and publish them to the shop</span>
          </span>
        </button>
      )}

      <div className="bg-white rounded-3xl border border-slate-200/80 overflow-x-auto">
        {statusFilter === "draft" ? (
          <div data-testid="draft-pricing-panel">
            <div className="px-5 py-4 border-b border-slate-100 bg-gold/5 flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-bold text-navy">Quick price entry — {shown.length} imported item{shown.length !== 1 ? "s" : ""} awaiting pricing</p>
                <p className="text-xs text-slate-500 mt-0.5">Enter the selling price and stock, then Publish — the item goes live in the shop and on every school list that references it. Cost price is optional (used for profit tracking).</p>
              </div>
              <button onClick={() => { setBulkOpen(true); setBulkPreview(null); }} className="h-9 px-4 rounded-full bg-navy text-white text-xs font-bold whitespace-nowrap flex items-center gap-1.5" data-testid="bulk-prices-open">
                <Upload size={13} /> Bulk price list
              </button>
            </div>
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-3">Item</th>
                  <th className="px-3 py-3">Category</th>
                  <th className="px-3 py-3 w-32">Sell price (R)</th>
                  <th className="px-3 py-3 w-24">Stock</th>
                  <th className="px-3 py-3 w-28">Cost (R)</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => {
                  const v = quick[p.id] || {};
                  return (
                    <tr key={p.id} className="border-b border-slate-50 hover:bg-cream/50" data-testid={`draft-row-${p.sku || p.id}`}>
                      <td className="px-5 py-3">
                        <p className="font-semibold text-navy">{p.name}</p>
                        <p className="text-xs text-slate-400 font-mono">{p.sku}{p.subcategory ? ` · ${p.subcategory}` : ""}</p>
                      </td>
                      <td className="px-3 py-3 text-slate-600 text-xs">{bySlug[p.category]?.name || p.category}</td>
                      <td className="px-3 py-3">
                        <input value={v.price ?? ""} onChange={(e) => qset(p.id, { price: e.target.value.replace(/[^0-9.]/g, "") })} inputMode="decimal" placeholder="0.00" className="w-full h-9 rounded-lg border border-slate-200 px-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-gold" data-testid={`draft-price-${p.sku || p.id}`} />
                      </td>
                      <td className="px-3 py-3">
                        <input value={v.stock ?? ""} onChange={(e) => qset(p.id, { stock: e.target.value.replace(/\D/g, "") })} inputMode="numeric" placeholder={String(p.stock)} className="w-full h-9 rounded-lg border border-slate-200 px-2.5 text-sm font-mono text-center outline-none focus:ring-2 focus:ring-gold" data-testid={`draft-stock-${p.sku || p.id}`} />
                      </td>
                      <td className="px-3 py-3">
                        <input value={v.cost_price ?? ""} onChange={(e) => qset(p.id, { cost_price: e.target.value.replace(/[^0-9.]/g, "") })} inputMode="decimal" placeholder="optional" className="w-full h-9 rounded-lg border border-slate-200 px-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-gold" data-testid={`draft-cost-${p.sku || p.id}`} />
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <button onClick={() => publish(p)} disabled={publishing === p.id} className="h-8 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50" data-testid={`draft-publish-${p.sku || p.id}`}>
                          {publishing === p.id ? "Publishing…" : "Publish"}
                        </button>
                        <button onClick={() => setEditing(toForm(p))} className="p-2 text-slate-400 hover:text-navy align-middle" data-testid={`edit-${p.sku || p.id}`} aria-label="Full edit">
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => remove(p)} className="p-2 text-slate-400 hover:text-red-500 align-middle" data-testid={`delete-${p.sku || p.id}`} aria-label="Discontinue">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {shown.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-400" data-testid="drafts-empty">No drafts waiting — everything imported has been priced.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
        <table className="w-full text-sm min-w-[760px]" data-testid="products-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="px-5 py-3">Product</th>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Price</th>
              <th className="px-5 py-3">Stock</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id} className="border-b border-slate-50 hover:bg-cream/50" data-testid={`product-row-${p.sku || p.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {p.image ? (
                      <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-100" />
                    ) : (
                      <span className="w-10 h-10 rounded-lg border border-dashed border-amber-300 bg-amber-50 flex items-center justify-center text-amber-500" title="No photo">
                        <ImageOff size={14} />
                      </span>
                    )}
                    <div>
                      <p className="font-semibold text-navy">{p.name}</p>
                      <p className="text-xs text-slate-400 font-mono">{p.sku}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-600">{bySlug[p.category]?.name || p.category}</td>
                <td className="px-5 py-3 font-mono">
                  {p.on_special ? (
                    <span>
                      <span className="line-through text-slate-400 mr-1.5">{formatR(p.price)}</span>
                      <span className="text-brand font-bold">{formatR(p.promo_price)}</span>
                    </span>
                  ) : (
                    formatR(p.price)
                  )}
                </td>
                <td className={`px-5 py-3 font-mono ${p.stock <= 10 ? "text-red-500 font-bold" : ""}`} data-testid={`stock-${p.sku || p.id}`}>{p.stock}</td>
                <td className="px-5 py-3">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${p.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{p.status}</span>
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => setEditing(toForm(p))} className="p-2 text-slate-400 hover:text-navy" data-testid={`edit-${p.sku || p.id}`} aria-label="Edit">
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => remove(p)} className="p-2 text-slate-400 hover:text-red-500" data-testid={`delete-${p.sku || p.id}`} aria-label="Discontinue">
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {bulkOpen && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-navy-dark/60 backdrop-blur-sm" onClick={() => setBulkOpen(false)} />
          <div className="relative bg-white rounded-3xl p-6 sm:p-8 w-full max-w-2xl my-10" data-testid="bulk-prices-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-navy text-lg">Bulk price list</h3>
              <button type="button" onClick={() => setBulkOpen(false)} data-testid="bulk-prices-close" aria-label="Close"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Paste the supplier price list — one line per item: <span className="font-mono font-bold text-navy">Item name | Price</span> or
              <span className="font-mono font-bold text-navy"> Item name | Price | Stock</span>. SKUs work too, and "R89,99" style prices are fine.
              Only lines matching a draft (unpriced) item are published — review the matches before applying.
            </p>
            <textarea
              value={bulkText}
              onChange={(e) => { setBulkText(e.target.value); setBulkPreview(null); }}
              rows={7}
              placeholder={"Matilda - Roald Dahl (Novel) | 89,99\nBusiness Studies Study Guide | 149.99 | 12\nIMP-1020 | 32.50 | 40"}
              className="w-full rounded-xl border border-slate-200 p-3 text-sm font-mono outline-none focus:ring-2 focus:ring-gold"
              data-testid="bulk-prices-input"
            />
            <div className="flex gap-2 mt-3">
              <button onClick={() => runBulk(false)} disabled={bulkBusy} className="h-9 px-5 rounded-full bg-white border border-slate-200 text-xs font-bold text-navy disabled:opacity-50" data-testid="bulk-prices-preview-btn">
                {bulkBusy && !bulkPreview ? "Checking…" : "Preview matches"}
              </button>
              {bulkPreview && bulkPreview.matched > 0 && (
                <button onClick={() => runBulk(true)} disabled={bulkBusy} className="h-9 px-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50" data-testid="bulk-prices-apply">
                  {bulkBusy ? "Publishing…" : `Publish ${bulkPreview.matched} item${bulkPreview.matched !== 1 ? "s" : ""}`}
                </button>
              )}
            </div>
            {bulkPreview && (
              <div className="mt-4 max-h-64 overflow-y-auto rounded-xl border border-slate-100" data-testid="bulk-prices-preview">
                <p className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 sticky top-0">
                  {bulkPreview.matched} of {bulkPreview.total} lines matched
                </p>
                {bulkPreview.results.map((r, i) => (
                  <div key={i} className="px-3 py-2 border-t border-slate-50 text-xs flex items-center gap-2 flex-wrap" data-testid={`bulk-price-row-${i}`}>
                    {r.match ? (
                      <>
                        <span className="text-emerald-600 font-bold">✓</span>
                        <span className="text-slate-500 flex-1 min-w-[140px]">{r.key}</span>
                        <span className="font-semibold text-navy">→ {r.match.name}</span>
                        <span className="font-mono font-bold text-navy">{formatR(r.price)}</span>
                        {r.stock !== null && r.stock !== undefined && <span className="text-slate-400">stock {r.stock}</span>}
                      </>
                    ) : (
                      <>
                        <span className="text-rose-500 font-bold">✕</span>
                        <span className="text-slate-500 flex-1">{r.line}</span>
                        <span className="text-rose-500">{r.note}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-navy-dark/60 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="relative bg-white rounded-3xl p-8 w-full max-w-2xl my-10" data-testid="product-form">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-bold text-navy text-lg">{editing.id ? "Edit product" : "New product"}</h3>
              <button type="button" onClick={() => setEditing(null)} data-testid="product-form-close" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {field("Name", "name", { required: true })}
              {field("SKU", "sku")}
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Category</span>
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none" data-testid="product-form-category">
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
              </label>
              {field("Subcategory", "subcategory")}
              {field("Barcode", "barcode")}
              {field("ISBN", "isbn")}
              {field("Selling price (R)", "price", { required: true, type: "number", step: "0.01", min: "0" })}
              {field("Cost price (R, admin only)", "cost_price", { type: "number", step: "0.01", min: "0" })}
              {field("Promo price (R)", "promo_price", { type: "number", step: "0.01", min: "0" })}
              {field("Stock on hand", "stock", { type: "number", min: "0" })}
              {field("Promo start (ISO, optional)", "promo_start", { placeholder: "2026-01-01T00:00:00Z" })}
              {field("Promo end (ISO, optional)", "promo_end", { placeholder: "2026-02-01T00:00:00Z" })}
              {field("Supplier", "supplier")}
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Status</span>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none" data-testid="product-form-status">
                  <option value="active">Active</option>
                  <option value="draft">Draft (hidden until priced)</option>
                  <option value="inactive">Inactive</option>
                  <option value="discontinued">Discontinued</option>
                </select>
              </label>
            </div>

            <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Customer option dropdown (optional)</span>
              <p className="text-[10px] text-slate-400 mt-0.5 mb-2">For products with choices — e.g. pen colours or pencil grades. Customers must pick one before adding to cart.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] font-bold text-slate-400">Dropdown label</span>
                  <input value={editing.variant_label || ""} onChange={(e) => setEditing({ ...editing, variant_label: e.target.value })} placeholder="Select Pen Colour" className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none" data-testid="product-form-variant-label" />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold text-slate-400">Options (comma-separated)</span>
                  <input value={editing.variant_options || ""} onChange={(e) => setEditing({ ...editing, variant_options: e.target.value })} placeholder="Blue, Black, Red" className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none" data-testid="product-form-variant-options" />
                </label>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">Clear both fields to remove the dropdown from this product.</p>
            </div>

            <div className="mt-4">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Product image</span>
              <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="h-10 px-4 rounded-lg bg-cream border border-slate-200 text-xs font-bold flex items-center gap-1.5 hover:border-gold transition-colors disabled:opacity-50"
                  data-testid="product-form-upload"
                >
                  <Upload size={14} /> {uploading ? "Uploading…" : "Upload from computer"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0])} data-testid="product-form-file" />
                <input
                  value={editing.image}
                  onChange={(e) => setEditing({ ...editing, image: e.target.value })}
                  placeholder="…or paste an image URL (blank = category default)"
                  className="flex-1 min-w-[220px] h-10 rounded-lg border border-slate-200 px-3 text-xs font-mono outline-none focus:ring-2 focus:ring-gold"
                  data-testid="product-form-image"
                />
                {editing.image && <img src={editing.image} alt="preview" className="h-12 w-12 rounded-lg object-cover border border-slate-200" data-testid="product-form-image-preview" />}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm mt-5">
              <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="w-4 h-4 accent-[#E8722A]" data-testid="product-form-featured" />
              Featured on home page
            </label>
            <label className="block mt-4">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Description</span>
              <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" data-testid="product-form-description" />
            </label>
            <button type="submit" className="mt-6 h-11 px-8 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm transition-colors" data-testid="product-form-save">
              Save & publish
            </button>
          </form>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-navy-dark/60 backdrop-blur-sm" onClick={() => setImportOpen(false)} />
          <div className="relative bg-white rounded-3xl p-8 w-full max-w-xl my-10" data-testid="import-modal">
            <h3 className="font-display font-bold text-navy text-lg">Bulk product import</h3>
            <div className="mt-4 rounded-2xl border-2 border-dashed border-slate-200 p-5 text-center">
              <Upload size={20} className="mx-auto text-slate-400 mb-2" />
              <p className="text-sm font-semibold text-navy">Upload a file — CSV, Excel, PDF or Word</p>
              <p className="text-[11px] text-slate-400 mt-1">You'll review new vs existing items before anything changes</p>
              <button onClick={importFile} className="mt-3 h-11 px-6 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm transition-colors" data-testid="import-file-btn">
                Choose file
              </button>
            </div>
            <div className="flex items-center gap-3 my-4">
              <span className="flex-1 h-px bg-slate-200" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">or paste CSV text</span>
              <span className="flex-1 h-px bg-slate-200" />
            </div>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              Columns: <code className="font-mono">sku,name,category,price,stock,cost_price,promo_price,barcode,isbn,description,image</code>.
              Existing SKUs get price/stock updates only — descriptions are never overwritten.
            </p>
            <textarea
              rows={8}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder={"sku,name,category,price,stock\nSTAT-100,Example Pen,stationery,19.99,50"}
              className="mt-4 w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-mono outline-none focus:ring-2 focus:ring-gold"
              data-testid="import-csv-text"
            />
            <div className="flex gap-3 mt-5">
              <button onClick={runImport} className="h-11 px-6 rounded-full bg-navy text-white font-bold text-sm" data-testid="import-run-btn">Run import</button>
              <button onClick={() => setImportOpen(false)} className="h-11 px-6 rounded-full border border-slate-200 font-semibold text-sm" data-testid="import-cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {reviewOpen && (
        <ImportReviewModal
          onClose={() => setReviewOpen(false)}
          onDone={() => { setReviewOpen(false); load(); }}
        />
      )}
    </div>
  );
}
